import { ReactElement, forwardRef } from "react";

import { radioButtonClass, rootClass } from "./radio-button.css";

export interface RadioButtonProps {
  /**
   * ID used for accessibility
   */
  id: string;
  /**
   * Type
   */
  type?: string;
  /**
   * Is the Button disabled?
   */
  isDisabled?: boolean;
  /**
   * Is button selected
   */
  isSelected: boolean;
  /**
   * onChange handler
   */
  onChange?: (s: string, type: string) => void;
  /**
   * onKeyDown handler
   */
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  /**
   * onBlur handler
   */
  onBlur?: (e: React.FocusEvent<HTMLDivElement, Element>) => void;
  /**
   * onFocus handler
   */
  onFocus?: (e: React.FocusEvent<HTMLDivElement, Element>) => void;
  /**
   * Label to display with radio button
   */
  title?: ReactElement | string;
  /**
   * Title component
   */
  tabIndex?: number;
  /**
   * Value of radio button
   */
  value: string;
  /**
   * radio group name
   */
  name?: string;
}

export const RadioButton = forwardRef<HTMLInputElement, RadioButtonProps>(
  (
    {
      id,
      isDisabled,
      isSelected,
      onChange,
      onKeyDown,
      onFocus,
      onBlur,
      tabIndex,
      title,
      value,
      name,
    }: RadioButtonProps,
    ref
  ) => {
    return (
      <div className={`${rootClass}`} data-testid={`${id}-radioButtonWrapper`}>
        <input
          data-testid={id}
          checked={isSelected}
          aria-checked={isSelected}
          disabled={isDisabled}
          id={id}
          type="radio"
          name={name}
          onKeyDown={onKeyDown}
          onFocus={onFocus}
          onBlur={onBlur}
          ref={ref}
          value={value}
          tabIndex={isSelected ? tabIndex : -1}
          className={radioButtonClass}
          onChange={
            onChange ? (e) => onChange(e.target.value, e.type) : () => {}
          }
        />
        {title}
      </div>
    );
  }
);

RadioButton.displayName = "RadioButton";
